import { useQueries } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import api from '../lib/api';
import { setupStepDefinitions, type SetupStepDefinition } from '../config/setupSteps';
import { useHelpPreferences } from './useHelpPreferences';

// ============================================
// TYPES
// ============================================

export interface SetupStepStatus {
  definition: SetupStepDefinition;
  completed: boolean;
  isLoading: boolean;
}

export interface SetupProgressResult {
  steps: SetupStepStatus[];
  completedCount: number;
  totalSteps: number;
  percentage: number;
  isAllComplete: boolean;
  isLoading: boolean;
  isSetupDismissed: boolean;
  dismissSetup: () => void;
}

// ============================================
// STEP COMPLETION CHECKERS
// ============================================

/**
 * Determina se um step está completo baseado na resposta do endpoint.
 * Cada step tem lógica específica de verificação.
 */
function isStepComplete(stepId: string, data: unknown): boolean {
  if (data == null) return false;

  switch (stepId) {
    case 'setup-tenant':
      // Tenant configurado se retorna dados
      return !!data;

    case 'setup-users':
      // Pelo menos 1 usuário adicional (array com length > 1)
      return Array.isArray(data) && data.length > 1;

    case 'setup-provider':
      // Pelo menos 1 provider instance configurado
      return Array.isArray(data) && data.length > 0;

    case 'setup-modules':
      // Pelo menos 1 módulo ativado
      return Array.isArray(data) && data.length > 0;

    case 'setup-fees':
      // Pelo menos 1 regra de taxa configurada
      return Array.isArray(data) && data.length > 0;

    case 'setup-theme':
      // Tema personalizado se retorna dados
      return !!data;

    default:
      return false;
  }
}

// ============================================
// HOOK
// ============================================

export function useSetupProgress(): SetupProgressResult {
  const { preferences, updatePreferences } = useHelpPreferences();

  // Consulta paralela para cada step
  const queries = useQueries({
    queries: setupStepDefinitions.map((step) => ({
      queryKey: ['setup-progress', step.id],
      queryFn: async () => {
        const { data } = await api.get(step.checkEndpoint);
        return data;
      },
      staleTime: 2 * 60 * 1000, // 2 minutos
      retry: 1,
      refetchOnWindowFocus: false,
    })),
  });

  // Monta o status de cada step
  const steps: SetupStepStatus[] = useMemo(
    () =>
      setupStepDefinitions.map((definition, index) => {
        const query = queries[index];
        return {
          definition,
          completed: query.isSuccess ? isStepComplete(definition.id, query.data) : false,
          isLoading: query.isLoading,
        };
      }),
    [queries],
  );

  const completedCount = useMemo(
    () => steps.filter((s) => s.completed).length,
    [steps],
  );

  const totalSteps = setupStepDefinitions.length;

  const percentage = useMemo(
    () => Math.round((completedCount / totalSteps) * 100),
    [completedCount, totalSteps],
  );

  const isAllComplete = completedCount === totalSteps;

  const isLoading = queries.some((q) => q.isLoading);

  const isSetupDismissed = preferences.setupDismissed;

  const dismissSetup = useCallback(() => {
    updatePreferences({ setupDismissed: true });
  }, [updatePreferences]);

  return {
    steps,
    completedCount,
    totalSteps,
    percentage,
    isAllComplete,
    isLoading,
    isSetupDismissed,
    dismissSetup,
  };
}
